import { Component, OnInit, Input, EventEmitter, Output, SimpleChanges, OnChanges, OnDestroy } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { ActiveCall } from 'app/models/dto/call.model';
import { DirectoryListItem } from 'app/models/dto/directory-list-item.model';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { CountryDataState } from 'ng2-app/store/country/country-data.state';
import { Store } from '@ngxs/store';
import { LocaleSettingService } from 'app/services/i18n/locale-setting.service';
import { Subject } from 'rxjs';
import { filter, map, takeUntil, tap } from 'rxjs/operators';

@Component({
  selector: 'app-softphone-transfer',
  templateUrl: './softphone-transfer.component.html',
  styleUrls: ['./softphone-transfer.component.scss'],
  standalone: false
})

export class SoftphoneTransferComponent implements OnInit, OnChanges, OnDestroy {

  @Input() call: ActiveCall;
  @Input() callStateIsProcessing: boolean;
  @Input() lendioDirectoryIsProcessing: boolean;
  @Input() type: string;
  @Input() lendioDirectory: DirectoryListItem[];
  @Output() coldTransfer = new EventEmitter();
  @Output() warmTransfer = new EventEmitter();
  @Output() getLendioDirectory = new EventEmitter();

  selectedUserName = '';
  selectedTeamName = '';
  twilioNumber = '';
  showLendioDirectory = false;
  columnsToDisplay = ['fullName', 'activeCall', 'teamName'];
  destroyed$ = new Subject<boolean>();
  phoneCountryCode: number | null = null;

  transferCallModel: any = {
    phone: {
      phone: null,
      countryCode: this.localeSettingService.getDefaultLocalePhoneCode()
    }
  };
  transferCallForm = new UntypedFormGroup({});
  transferCallOptions: FormlyFormOptions = {};
  transferCallFields: FormlyFieldConfig[] = [
    {
      key: 'phone',
      type: 'intl-phone',
      templateOptions: {
        label: 'Phone',
        placeholder: 'Enter Number',
        attributes: {
          'data-cy': 'transfer-call-phone-input'
        }
      }
    }
  ];

  searchDirectoryForm = new UntypedFormGroup({
    fullName: new UntypedFormControl(''),
    teamName: new UntypedFormControl(''),
  });

  dataSource: MatTableDataSource<DirectoryListItem> = new MatTableDataSource<DirectoryListItem>();
  selectedLenderUserIndex: number | null = null;

  constructor(
    private localeSettingService: LocaleSettingService,
    private store: Store,
  ) {}

  ngOnInit() {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes.lendioDirectory && this.lendioDirectory?.length > 0) {
      this.dataSource = new MatTableDataSource(this.lendioDirectory);
    }
  }

  directoryTrackBy(directoryListItem: DirectoryListItem): number {
    return directoryListItem.lenderUserId;
  }

  // This only filters if the Lendio Directory is loaded.
  filterDirectoryByPhoneNumber(search: string): void {
    if (this.showLendioDirectory) {
      search = search.replace(/\D+/g, '');
      const pattern = new RegExp('^\\d+$');
      const filterValue = search.trim();
      if (filterValue.match(pattern) || search === '') {
        this.dataSource.filter = filterValue;
      }
    }
  }

  setPhoneCountryCode(phoneCountryCode: string) {
    const countryCodeIdValue = phoneCountryCode ? phoneCountryCode : '2';
    this.store.select(CountryDataState.countryFromPhoneCountyCodeId)
      .pipe(
        takeUntil(this.destroyed$),
        map(filterFn => filterFn(countryCodeIdValue))
      )
      .subscribe((countryData) => {
        if (countryData) {
          this.phoneCountryCode = countryData.phoneCountryCode?.countryCode || null;
        }
      });
  }

  processColdTransfer(callId: number) {
    this.setPhoneCountryCode(this.transferCallForm.value.phone.countryCode);
    const twilioNumber = `+${this.phoneCountryCode}${this.transferCallForm.value.phone.phone}`;
    const callInfo = { twilioNumber, callId };
    this.coldTransfer.emit(callInfo);
  }

  processWarmTransfer(callId: number) {
    this.setPhoneCountryCode(this.transferCallForm.value.phone.countryCode);
    const twilioNumber = `+${this.phoneCountryCode}${this.transferCallForm.value.phone.phone}`;
    const callInfo = { twilioNumber, callId };
    this.warmTransfer.emit(callInfo);
  }

  loadLendioDirectory() {
    this.showLendioDirectory = true;
    this.getLendioDirectory.emit();
    this.searchDirectoryForm.get('fullName')?.valueChanges.pipe(
      takeUntil(this.destroyed$),
      filter(search => typeof search === 'string'),
      tap(search => {
        const filterValue = search.trim();
        this.dataSource.filter = filterValue;
      }),
    ).subscribe();
  }

  selectLenderUser(row: DirectoryListItem): void {
    if (this.selectedLenderUserIndex === row.lenderUserId) {
      this.clearFilters();
    } else {
      this.selectedLenderUserIndex = row.lenderUserId;
      this.transferCallForm.get('phone.phone')?.setValue(row.twilioNumber.substring(2));
      this.selectedUserName = row.fullName;
      this.selectedTeamName = row.teamName;
      this.searchDirectoryForm.patchValue({
        teamName: this.selectedTeamName,
        fullName: this.selectedUserName,
      });
    }
  }

  clearFilters(): void {
    this.dataSource.filter = '';
    this.selectedLenderUserIndex = null;
    this.transferCallForm.get('phone.phone')?.reset();
    this.searchDirectoryForm.reset();
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

}
